// Admin PWA entry — routes + auth guard, mounted into #app (hash mode).
import { html, route, router } from './shared/t4.js';
import './shared/theme.js';   // apply saved/OS theme before first render
import { noteRouteChange } from './shared/dashnav.js';
import { initGlobalShortcuts } from './shared/globalkb.js';
import { initGlobalSearch } from './shared/globalsearch.js';
import { isAuthed, isAdmin, inCompaniesMode, isPartnerAdmin, isPlatformAdmin } from './shared/auth.js';
import { impersonationActive } from './shared/impersonate.js';
import { initI18n } from './shared/i18n.js';
import { loginScreen } from './admin/login.js';
import { registerScreen } from './admin/register.js';
import { forgotScreen } from './admin/forgot.js';
import { resetScreen } from './admin/reset.js';
import { dashboardScreen } from './admin/dashboard.js';
import { administratorsScreen } from './admin/administrators.js';
import { bugsScreen } from './admin/bugs.js';
import { initBugConsoleTap } from './shared/bugreport.js';
import { installMoneyInputHygiene } from './shared/moneyinput.js';
import { initPwaInstall } from './shared/pwa-install.js';
import { categoriesScreen } from './admin/categories.js';
import { countriesScreen } from './admin/countries.js';
import { countryDetailScreen } from './admin/country_detail.js';
import { languagesScreen } from './admin/languages.js';
import { languageDetailScreen } from './admin/language_detail.js';
import { forexScreen } from './admin/forex.js';
import { companiesScreen } from './admin/companies.js';
import { usersScreen } from './admin/users.js';
import { userDetailScreen } from './admin/user_detail.js';
import { billingScreen } from './admin/billing.js';
import { partnersScreen } from './admin/partners.js';
import { tenantHomeScreen } from './tenant/home.js';
import { billingReturnScreen, billingCancelScreen } from './tenant/billing.js';
import { importScreen } from './tenant/import.js';
import { chartScreen } from './tenant/chart.js';
import { notFoundScreen } from './notfound.js';
import { cashbookScreen } from './tenant/cashbook.js';
import { bankRulesScreen } from './tenant/bank_rules.js';
import { statementsScreen } from './tenant/statements.js';
import { journalsScreen } from './tenant/journals.js';
import { governanceScreen } from './tenant/governance.js';
import { contactsScreen, contactDetailScreen } from './tenant/contacts.js';
import { documentsListScreen, DOC_TYPES } from './tenant/documents.js';
import { salesMemosScreen, salesMemoDetailScreen } from './tenant/sales_memos.js';
import { documentDetailScreen } from './tenant/document_detail.js';
import { documentEditorScreen } from './tenant/document_editor.js';
import { batchesScreen } from './tenant/batches.js';
import { approvalsScreen } from './tenant/approvals.js';
import { reportsScreen, reportViewScreen } from './tenant/reports.js';
import { tenantDashboardScreen } from './tenant/dashboard.js';
import { settingsScreen } from './tenant/settings.js';
import { productsScreen } from './tenant/products.js';
import { recurringScreen } from './tenant/recurring.js';
import { expensesScreen } from './tenant/expenses.js';
import { teamScreen } from './tenant/team.js';
import { tenantShell } from './tenant/tshell.js';
import { portalScreen } from './tenant/portal.js';
import { publicDocScreen } from './tenant/public_doc.js';

// Keep platform conventions in the shared shell rather than duplicating them in screen code.
// macOS places Cancel before the commitment; Windows places the commitment before Cancel.
const platform = String(navigator.userAgentData?.platform || navigator.platform || '').toLowerCase();
document.documentElement.classList.add(platform.includes('win') ? 'platform-windows'
  : platform.includes('mac') ? 'platform-mac' : 'platform-other');

// after login the two audiences split: admins → the platform console (/), tenants → their home
// (/home). An admin holding a LIVE support-access grant (shared/impersonate.js) is treated as a
// tenant for the grant's hour: the tenant guard admits them, and the admin guard bounces them to
// /home until they Exit — their own Keycloak realm (isAdmin()) never changes while impersonating.
// An admin who signed in on the COMPANIES side (inCompaniesMode — #8 unified login) is likewise
// treated as a tenant for routing: their sleek-admin token reaches the tenant app (the API accepts
// it) and belongs on /home, not the console. isAdmin() still holds, so the admin surface stays open.
const onTenantSide = () => impersonationActive() || inCompaniesMode();
// A REGIONAL PARTNER (partner admin, not a global platform admin) uses the console too, but only its
// region-scoped screens (Phase 1C). They are ADMITTED to the console and land on the dashboard like
// any admin; the global-only screens (countries, languages, forex, partners, administrators, ledger
// categories) are not theirs — `partnerBlocked` sends them back to the dashboard, and the admin API
// scopes every list they CAN see to their country server-side.
const partnerOnlyAdmin = () => isPartnerAdmin() && !isPlatformAdmin();
const home = () => (isAdmin() && !onTenantSide()) ? '/' : '/home';
const guard       = () => !isAuthed() ? '/login' : (impersonationActive() ? '/home' : (isAdmin() ? true : '/home'));   // admin screens (scoped + global)
const partnerBlocked = () => !isAuthed() ? '/login' : (impersonationActive() ? '/home' : (partnerOnlyAdmin() ? '/' : (isAdmin() ? true : '/home')));   // GLOBAL-only screens — a Regional Partner is sent to the dashboard
const tenantGuard = () => !isAuthed() ? '/login' : ((isAdmin() && !onTenantSide()) ? '/' : true);                       // tenant-only screens

// PUBLIC contact-onboarding portal — the token is the whole authorisation (no login)
route('/portal/{token}', { handler: (p) => portalScreen(p.token) });
// PUBLIC customer document page (quote approve/decline, invoice view/pay) — same token model
route('/d/{token}', { handler: (p) => publicDocScreen(p.token) });

route('/login',    { guard: () => isAuthed() ? home() : true, handler: () => loginScreen() });
route('/register', { guard: () => isAuthed() ? home() : true, handler: () => registerScreen() });
route('/forgot',   { guard: () => isAuthed() ? home() : true, handler: () => forgotScreen() });
route('/reset-password/{token}', { handler: (p) => resetScreen(p.token) });

route('/home', { guard: tenantGuard, handler: () => tenantHomeScreen() });
route('/billing/return', { guard: tenantGuard, handler: () => billingReturnScreen() });
route('/billing/cancel', { guard: tenantGuard, handler: () => billingCancelScreen() });
// Offered AFTER billing activation (billing/return) and permanently from the company card.
route('/companies/{id}/import', { guard: tenantGuard, handler: (p) => importScreen(p.id) });
// Read-only chart of accounts — the setup confirmation, and the target side of an import mapping.
// NOT gated on billing: seeing what your company was set up with is never something you pay for.
route('/companies/{id}/accounts', { guard: tenantGuard, handler: (p) => chartScreen(p.id) });
route('/co/{id}/bank', { guard: tenantGuard, handler: (p) => cashbookScreen(p.id) });
route('/co/{id}/bank/{accountId}/statements', { guard: tenantGuard, handler: (p) => statementsScreen(p.id, p.accountId) });
route('/co/{id}/bank/{accountId}', { guard: tenantGuard, handler: (p) => cashbookScreen(p.id, p.accountId) });
route('/co/{id}/bank-rules', { guard: tenantGuard, handler: (p) => bankRulesScreen(p.id) });
route('/co/{id}/journals', { guard: tenantGuard, handler: (p) => journalsScreen(p.id) });
route('/co/{id}/governance', { guard: tenantGuard, handler: (p) => governanceScreen(p.id) });
route('/co/{id}/sales-memos', { guard: tenantGuard, handler: (p) => salesMemosScreen(p.id) });
route('/co/{id}/sales-memos/{memoId}', { guard: tenantGuard, handler: (p) => salesMemoDetailScreen(p.id,p.memoId) });
// The remaining "books" screens (v2 parity) — placeholders until each lands this build.
const tenantSoon = (id, title) => tenantShell(id, title,
  html`<div class="page"><div class="page-head"><div><h1 class="page-title">${title}</h1></div></div>
    <div class="muted-block">Coming next.</div></div>`);
route('/co/{id}/dashboard', { guard: tenantGuard, handler: (p) => tenantDashboardScreen(p.id) });
route('/co/{id}/contacts',  { guard: tenantGuard, handler: (p) => contactsScreen(p.id) });
route('/co/{id}/contacts/{cid}', { guard: tenantGuard, handler: (p) => contactDetailScreen(p.id, p.cid) });
route('/co/{id}/reports', { guard: tenantGuard, handler: (p) => reportsScreen(p.id) });
route('/co/{id}/reports/{code}', { guard: tenantGuard, handler: (p) => reportViewScreen(p.id, p.code) });
route('/co/{id}/team', { guard: tenantGuard, handler: (p) => teamScreen(p.id) });
route('/co/{id}/settings', { guard: tenantGuard, handler: (p) => settingsScreen(p.id) });
// Deep-link a specific settings tab in-app (the setup guide lands the logo step on Branding, etc.).
// Path-based because navigate() drops ?query (CLAUDE.md); the tab is validated in settingsScreen.
route('/co/{id}/settings/{tab}', { guard: tenantGuard, handler: (p) => settingsScreen(p.id, p.tab) });
// Products & Services — promoted from Settings → Stock to its own top-level nav item (documents
// parity sweep Wave B, #22). Reuses buildStockTab via products.js; see tshell.js's `products` TNAV entry.
route('/co/{id}/products', { guard: tenantGuard, handler: (p) => productsScreen(p.id) });

// Documents (Sales & Purchases) — the invoiceTest Documents port (owner 2026-09-02). Explicit
// routes per doc key (invoices/quotes/credit-notes/bills/purchase-orders/goods-received/
// supplier-credits) so there is no catch-all shadowing the screens above.
Object.keys(DOC_TYPES).forEach((doc) => {
  route('/co/{id}/' + doc + '/new', { guard: tenantGuard, handler: (p) => documentEditorScreen(p.id, doc, null) });
  route('/co/{id}/' + doc + '/{docId}/edit', { guard: tenantGuard, handler: (p) => documentEditorScreen(p.id, doc, p.docId) });
  route('/co/{id}/' + doc + '/{docId}', { guard: tenantGuard, handler: (p) => documentDetailScreen(p.id, doc, p.docId) });
  route('/co/{id}/' + doc, { guard: tenantGuard, handler: (p) => documentsListScreen(p.id, doc) });
});
// The Documents hub landing — the single "Documents" nav item points here; it opens on the
// Quotes tab (owner 2026-09-09 — first family in the Debtors flow, the natural start of the
// sales pipeline). Every /co/{id}/{doc} URL above renders the SAME hub with that family's tab
// active, so deep links, back-to-place (W7) and notification click-through (W8) all keep working.
route('/co/{id}/documents', { guard: tenantGuard, handler: (p) => documentsListScreen(p.id, 'quotes') });

// Batch invoicing (Workstream C) — list / template editor / run screen. Specific paths first
// so the {tid} pattern never shadows /run (mirrors the documents block above).
route('/co/{id}/batches/{tid}/run', { guard: tenantGuard, handler: (p) => batchesScreen(p.id, 'run', p.tid) });
route('/co/{id}/batches/{tid}',     { guard: tenantGuard, handler: (p) => batchesScreen(p.id, 'edit', p.tid) });
route('/co/{id}/batches',           { guard: tenantGuard, handler: (p) => batchesScreen(p.id) });
route('/co/{id}/recurring', { guard: tenantGuard, handler: (p) => recurringScreen(p.id) });
route('/co/{id}/expenses', { guard: tenantGuard, handler: (p) => expensesScreen(p.id) });

// Document approvals queue (Workstream A phase 6) — submit lives in the editor, decisions here.
route('/co/{id}/approvals', { guard: tenantGuard, handler: (p) => approvalsScreen(p.id) });
// The Notifications hub landing — the single "Notifications" nav item points here; it opens on the
// Approvals tab. /co/{id}/approvals and /co/{id}/expenses render the same hub with that tab active.
route('/co/{id}/notifications', { guard: tenantGuard, handler: (p) => approvalsScreen(p.id) });

route('/',           { guard, handler: () => dashboardScreen() });
route('/administrators', { guard: partnerBlocked, handler: () => administratorsScreen() });
route('/bugs',       { guard, handler: () => bugsScreen() });
route('/categories', { guard: partnerBlocked, handler: () => categoriesScreen('IFRS') });
route('/categories/gaap', { guard: partnerBlocked, handler: () => categoriesScreen('GAAP') });
route('/countries',  { guard: partnerBlocked, handler: () => countriesScreen() });
route('/countries/{id}', { guard: partnerBlocked, handler: (p) => countryDetailScreen(p.id) });
route('/languages',  { guard: partnerBlocked, handler: () => languagesScreen() });
route('/languages/{code}', { guard: partnerBlocked, handler: (p) => languageDetailScreen(p.code) });
route('/forex',      { guard: partnerBlocked, handler: () => forexScreen() });
route('/companies',  { guard, handler: () => companiesScreen() });
route('/users',      { guard, handler: () => usersScreen() });
route('/users/{id}', { guard, handler: (p) => userDetailScreen(p.id) });
route('/billing',    { guard, handler: () => billingScreen() });
route('/partners',   { guard: partnerBlocked, handler: () => partnersScreen() });

// Audience-aware — a tenant must never be shown the admin drawer (see src/notfound.js).
route('*', () => notFoundScreen());

// Onward navigation drops the "‹ Back to dashboard" return, so it only shows on the screen
// reached directly from the dashboard (src/shared/dashnav.js).
router.on('change', ({ path }) => noteRouteChange(path));

// Preload the company's translation bundle BEFORE the first render so keyed labels (t()) resolve in
// the company's language on load/reload. Words are ADMIN master data synced into reference.translation;
// the bundle is a COMPANY setting, and a language change reloads the app (see settings.js).
//
// URLs are PATH-based (owner 2026-09-02: no #). nginx + vite both fall back to index.html, so deep
// links reload fine. Legacy hash links (old bookmarks, already-emailed portal / reset links) still
// land: a #/… hash is swapped for its path before the router starts.
initBugConsoleTap();   // ring-buffer console errors so a bug report can carry the tail
initGlobalShortcuts(); // n = new invoice · q = new quote · g = dashboard — global, every /co/{id}/… screen (A4)
initGlobalSearch();    // Ctrl+K / Cmd+K / "/" — global search palette across docs/contacts/items (A7)
installMoneyInputHygiene(); // strip commas from every money / rate / discount input, app-wide
initPwaInstall();      // "Install aboo" banner (Android/desktop prompt + iOS steps) + service worker

async function boot(){
  if(location.hash.startsWith('#/')){
    history.replaceState(null, '', location.hash.slice(1));
  }
  const m = location.pathname.match(/^\/(?:co|companies)\/([^/?]+)/);
  if(m){ try { await initI18n(m[1]); } catch { /* falls back to English; tshell retries on mount */ } }
  router.start({ target:'#app', mode:'history' });
}
boot();
